import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { CircularProgress } from "@material-ui/core";
import {
  AuthNav,
  AdminSideNav,
} from "../../../components/Navbar/Webpages/AuthNav.jsx";
import {
  setCurrencyRateRequest,
  setSystemCurrencyRequest,
  getCurrencyRatesRequest,
  getSystemCurrencyRequest,
  deactivateCurrencyRateRequest,
  deleteCurrencyRateRequest,
  clearSetCurrencyRateSuccess,
  clearSetSystemCurrencySuccess,
} from "../../../redux/action";
import {
  CurrencyDollarIcon,
  CogIcon,
  PlusIcon,
  TrashIcon,
  EyeOffIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/outline";

const CurrencyManagement = () => {
  const dispatch = useDispatch();
  const [showAddRateForm, setShowAddRateForm] = useState(false);
  const [showSystemCurrencyForm, setShowSystemCurrencyForm] = useState(false);
  const [newRate, setNewRate] = useState({
    targetCurrency: "",
    rate: "",
  });
  const [newSystemCurrency, setNewSystemCurrency] = useState("");

  const {
    currencyRates,
    systemCurrency,
    isLoading,
    setCurrencyRateSuccess,
    setSystemCurrencySuccess,
    setCurrencyRateError,
    setSystemCurrencyError,
    getCurrencyRatesError,
    getSystemCurrencyError,
  } = useSelector((state) => {
    const {
      errors: {
        setCurrencyRate: setCurrencyRateError,
        setSystemCurrency: setSystemCurrencyError,
        getCurrencyRates: getCurrencyRatesError,
        getSystemCurrency: getSystemCurrencyError,
      },
    } = state.ajaxStatuses;

    const {
      setCurrencyRateLoading,
      setSystemCurrencyLoading,
      getCurrencyRatesLoading,
      getSystemCurrencyLoading,
    } = state.loadingIndicator;

    const {
      currencyRates,
      systemCurrency,
      setCurrencyRateSuccess,
      setSystemCurrencySuccess,
    } = state.currencyData;

    const isLoading =
      setCurrencyRateLoading ||
      setSystemCurrencyLoading ||
      getCurrencyRatesLoading ||
      getSystemCurrencyLoading;

    return {
      currencyRates: currencyRates || [],
      systemCurrency,
      isLoading,
      setCurrencyRateSuccess,
      setSystemCurrencySuccess,
      setCurrencyRateError,
      setSystemCurrencyError,
      getCurrencyRatesError,
      getSystemCurrencyError,
    };
  });

  useEffect(() => {
    dispatch(getCurrencyRatesRequest());
    dispatch(getSystemCurrencyRequest());
  }, [dispatch]);

  useEffect(() => {
    if (setCurrencyRateError) toast.error(setCurrencyRateError);
    if (setSystemCurrencyError) toast.error(setSystemCurrencyError);
    if (getCurrencyRatesError) toast.error(getCurrencyRatesError);
    if (getSystemCurrencyError) toast.error(getSystemCurrencyError);
  }, [
    setCurrencyRateError,
    setSystemCurrencyError,
    getCurrencyRatesError,
    getSystemCurrencyError,
  ]);

  useEffect(() => {
    if (setCurrencyRateSuccess) {
      toast.success("Currency rate updated successfully!");
      setShowAddRateForm(false);
      setNewRate({ targetCurrency: "", rate: "" });
      dispatch(clearSetCurrencyRateSuccess());
      dispatch(getCurrencyRatesRequest()); // Refresh rates
    }
  }, [setCurrencyRateSuccess, dispatch]);

  useEffect(() => {
    if (setSystemCurrencySuccess) {
      toast.success("System currency updated successfully!");
      setShowSystemCurrencyForm(false);
      setNewSystemCurrency("");
      dispatch(clearSetSystemCurrencySuccess());
      dispatch(getSystemCurrencyRequest()); // Refresh system currency
    }
  }, [setSystemCurrencySuccess, dispatch]);

  const handleAddRate = (e) => {
    e.preventDefault();
    if (!newRate.targetCurrency || !newRate.rate) {
      toast.error("Please fill in all fields");
      return;
    }
    dispatch(
      setCurrencyRateRequest({
        targetCurrency: newRate.targetCurrency.toUpperCase(),
        rate: parseFloat(newRate.rate),
      })
    );
  };

  const handleSetSystemCurrency = (e) => {
    e.preventDefault();
    if (!newSystemCurrency) {
      toast.error("Please enter a currency code");
      return;
    }
    dispatch(
      setSystemCurrencyRequest({
        currency: newSystemCurrency.toUpperCase(),
      })
    );
  };

  const handleDeactivateRate = (rateId) => {
    if (window.confirm("Are you sure you want to deactivate this rate?")) {
      dispatch(deactivateCurrencyRateRequest(rateId));
    }
  };

  const handleDeleteRate = (rateId) => {
    if (window.confirm("Are you sure you want to delete this rate?")) {
      dispatch(deleteCurrencyRateRequest(rateId));
    }
  };

  const currencySymbols = {
    USD: "$",
    ZMW: "K",
    EUR: "€",
    GBP: "£",
    NGN: "₦",
    GHS: "₵",
    KES: "KSh",
    UGX: "USh",
    TZS: "TSh",
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <AuthNav title={"Currency Management"} />
      <AdminSideNav />

      <div className="container max-w-7xl mx-auto px-4 py-8 mb-20 pt-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white rounded-full shadow-sm text-primary">
              <CurrencyDollarIcon className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-gray-900">
                Currency Management
              </h1>
              <p className="text-sm text-gray-500 font-body">
                Manage exchange rates and system currency
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setShowSystemCurrencyForm(!showSystemCurrencyForm)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
            >
              <CogIcon className="w-4 h-4" />
              Set System Currency
            </button>
            <button
              onClick={() => setShowAddRateForm(!showAddRateForm)}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Add Rate
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-32">
            <CircularProgress size={40} style={{ color: "#4F46E5" }} />
            <p className="mt-4 text-gray-500 font-medium">Loading...</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* System Currency Card */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-heading font-bold text-gray-900">
                  Current System Currency
                </h2>
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-green-600 font-medium">
                    Active
                  </span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-full">
                    <CurrencyDollarIcon className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">
                      Display Currency
                    </p>
                    <p className="text-3xl font-heading font-bold text-gray-900">
                      {systemCurrency || "USD"}{" "}
                      <span className="text-lg text-gray-500">
                        ({currencySymbols[systemCurrency] || "$"})
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Set System Currency Form */}
            {showSystemCurrencyForm && (
              <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-heading font-bold text-gray-900 mb-4">
                  Set System Currency
                </h3>
                <form onSubmit={handleSetSystemCurrency} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency Code
                    </label>
                    <select
                      value={newSystemCurrency}
                      onChange={(e) => setNewSystemCurrency(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
                    >
                      <option value="" disabled>
                        Select a currency (e.g., ZMW, USD)
                      </option>
                      <option value="USD">USD - US Dollar ($)</option>
                      <option value="ZMW">ZMW - Zambian Kwacha (K)</option>
                      {/* <option value="EUR">EUR - Euro (€)</option>
                      <option value="GBP">GBP - British Pound (£)</option>
                      <option value="NGN">NGN - Nigerian Naira (₦)</option>
                      <option value="GHS">GHS - Ghanaian Cedi (₵)</option>
                      <option value="KES">KES - Kenyan Shilling (KSh)</option>
                      <option value="UGX">UGX - Ugandan Shilling (USh)</option>
                      <option value="TZS">
                        TZS - Tanzanian Shilling (TSh)
                      </option>
                      <option value="ZAR">ZAR - South African Rand (R)</option>
                      <option value="BWP">BWP - Botswana Pula (P)</option>
                      <option value="MWK">MWK - Malawian Kwacha (MK)</option> */}
                    </select>
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      className="px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                    >
                      Update System Currency
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowSystemCurrencyForm(false)}
                      className="px-6 py-2 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Add Rate Form */}
            {showAddRateForm && (
              <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-heading font-bold text-gray-900 mb-4">
                  Add/Update Exchange Rate
                </h3>
                <form onSubmit={handleAddRate} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Target Currency
                      </label>
                      <input
                        type="text"
                        value={newRate.targetCurrency}
                        onChange={(e) =>
                          setNewRate({
                            ...newRate,
                            targetCurrency: e.target.value,
                          })
                        }
                        placeholder="e.g., ZMW, EUR, GBP"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                        maxLength={3}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Exchange Rate (from USD)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={newRate.rate}
                        onChange={(e) =>
                          setNewRate({ ...newRate, rate: e.target.value })
                        }
                        placeholder="e.g., 25.50"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      className="px-6 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
                    >
                      Add/Update Rate
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddRateForm(false)}
                      className="px-6 py-2 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Currency Rates Table */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <h3 className="text-lg font-heading font-bold text-gray-900">
                  Exchange Rates
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  All rates are based on USD as the base currency
                </p>
              </div>

              {currencyRates.length === 0 ? (
                <div className="p-12 text-center">
                  <CurrencyDollarIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 font-medium">
                    No exchange rates configured
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    Add your first exchange rate to get started
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                          Currency Pair
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                          Exchange Rate
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                          Last Updated
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {currencyRates.map((rate) => (
                        <tr key={rate._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-gray-100 rounded-lg">
                                <CurrencyDollarIcon className="w-4 h-4 text-gray-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">
                                  {rate.baseCurrency} → {rate.targetCurrency}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {currencySymbols[rate.baseCurrency] || ""} to{" "}
                                  {currencySymbols[rate.targetCurrency] || ""}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-lg font-bold text-gray-900">
                              {rate.rate.toFixed(2)}
                            </p>
                            <p className="text-sm text-gray-500">
                              1 {rate.baseCurrency} = {rate.rate.toFixed(2)}{" "}
                              {rate.targetCurrency}
                            </p>
                          </td>
                          <td className="px-6 py-4">
                            {rate.isActive ? (
                              <div className="flex items-center gap-2">
                                <CheckCircleIcon className="w-4 h-4 text-green-500" />
                                <span className="text-sm text-green-600 font-medium">
                                  Active
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <XCircleIcon className="w-4 h-4 text-red-500" />
                                <span className="text-sm text-red-600 font-medium">
                                  Inactive
                                </span>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-900">
                              {new Date(rate.updatedAt).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(rate.updatedAt).toLocaleTimeString()}
                            </p>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {rate.isActive && (
                                <button
                                  onClick={() => handleDeactivateRate(rate._id)}
                                  className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                                  title="Deactivate"
                                >
                                  <EyeOffIcon className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteRate(rate._id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrencyManagement;
